/** A task proposal stays unsaved until the user submits its displayed scope. */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../shared/ui/button';
import { createSITask, type SITaskProposal, type SICreatedTask } from './siTasks';
import type { SIProposalState } from './siActions';
import { refreshAITaskViews } from './refreshAITaskViews';
/** initialState comes from a reopened chat; a live reply's proposals start pending. */
export function SITaskProposalCard({ workspaceId, proposal, initialState = 'pending' }: { workspaceId: string; proposal: SITaskProposal; initialState?: SIProposalState }) {
 const [saved, setSaved] = useState<SICreatedTask | null>(null); const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
 const restoredDone = initialState === 'confirmed', expired = initialState === 'expired';
 const controller = useRef<AbortController | null>(null);
 useEffect(() => () => controller.current?.abort(), []);
 const create = async () => {
  if (busy || saved || restoredDone || expired) return;
  const abort = new AbortController(); controller.current = abort; setBusy(true); setError(null);
  try {
   const task = await createSITask(workspaceId, proposal, abort.signal);
   if (!abort.signal.aborted) {
    setSaved(task);
    const refreshError = await refreshAITaskViews();
    if (!abort.signal.aborted && refreshError) setError(refreshError);
   }
  } catch (cause) { if (!abort.signal.aborted) setError(cause instanceof Error ? cause.message : 'Task could not be saved.'); }
  finally { if (!abort.signal.aborted) setBusy(false); }
 };
 return <section aria-label="Task proposal" className="mb-4 rounded-lg border p-3 text-sm">
  <p className="text-xs text-muted-foreground">{saved || restoredDone ? 'Task saved' : 'Task proposal'} · {proposal.projectName}</p>
  <p className="mt-1 font-medium">{proposal.title}</p><p className="mt-1 whitespace-pre-wrap">{proposal.description}</p>
  <p className="mt-2 text-xs text-muted-foreground">{saved?.workflowNotice ?? proposal.workflowNotice}</p>
  {saved ? <a className="mt-2 block underline" href={`/projects/${encodeURIComponent(saved.projectId)}`}>Open project</a>
   : restoredDone ? <>
    <Button type="button" size="sm" className="mt-2" disabled>Confirmed</Button>
    <a className="mt-2 block underline" href={`/projects/${encodeURIComponent(proposal.projectId)}`}>Open project</a>
   </> : expired ? <Button type="button" size="sm" variant="outline" className="mt-2" disabled>Expired, ask again</Button>
   : <Button type="button" size="sm" className="mt-2" disabled={busy} onClick={() => void create()}>{busy ? 'Saving task...' : 'Create task'}</Button>}
  {error && <p role="alert" className="mt-2 text-destructive">{error}</p>}
 </section>;
}
